import { useEffect, useMemo, useRef, useState } from 'react';
import type { Timesheet, TimesheetStatus, TimesheetSummary } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE, TIMESHEET_STATUSES, mondayOf } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { DetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Modal } from '../../components/ui/Modal.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { formatDate } from '../../utils/date.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { useMemberOptions } from '../time-entries/api.js';
import { MemberMultiFilter } from '../members/MemberMultiFilter.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import { savedString, savedStringArray, useFilterDefaults } from '../filters/useFilterDefaults.js';
import { STATUS_LABELS, STATUS_TONES } from './status.js';
import { DueConfigCard } from './DueConfigCard.js';
import { DueIndicator } from './DueIndicator.js';
import {
  useSubmitTimesheet,
  useTimesheets,
  useTimesheetsSummary,
  usePreloadCurrentWeek,
} from './api.js';

/** Week (Monday..Sunday) or calendar-month range the list is narrowed to. */
interface QuickRange {
  mode: 'week' | 'month';
  /** Inclusive weekStartDate bounds, ISO dates. */
  from: string;
  to: string;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Week range anchored on the Monday containing `iso`. */
function weekRange(iso: string): QuickRange {
  const monday = mondayOf(iso);
  return { mode: 'week', from: monday, to: addDaysIso(monday, 6) };
}

/** Calendar-month range containing `iso` (timesheets whose Monday falls in the month). */
function monthRange(iso: string, shiftMonths = 0): QuickRange {
  const d = new Date(`${iso.slice(0, 7)}-01T00:00:00.000Z`);
  d.setUTCMonth(d.getUTCMonth() + shiftMonths);
  const from = d.toISOString().slice(0, 10);
  d.setUTCMonth(d.getUTCMonth() + 1);
  d.setUTCDate(0);
  return { mode: 'month', from, to: d.toISOString().slice(0, 10) };
}

/** Human label for the active quick range. */
function rangeLabel(range: QuickRange): string {
  if (range.mode === 'week') return `Week of ${formatDate(range.from)}`;
  return new Date(`${range.from}T00:00:00.000Z`).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Trims trailing zeros from an hours string for compact display. */
function formatHours(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return Number.isInteger(n) ? String(n) : Number(n.toFixed(2)).toString();
}

/** Billable share of the filtered hours, as a plain number string (no % sign). */
function percentBillable(billable: string, total: string): string {
  const t = Number(total);
  if (!Number.isFinite(t) || t === 0) return '0';
  return (((Number(billable) || 0) / t) * 100).toFixed(1);
}

/** One headline hours total in the summary strip. */
function TotalStat({
  label,
  value,
  isLoading,
  tone = 'text-slate-900',
  suffix = '',
}: {
  label: string;
  value: string | undefined;
  isLoading: boolean;
  tone?: string;
  suffix?: string;
}): JSX.Element {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-sm font-medium text-slate-600">{label}</span>
      <span className={`text-2xl font-bold tabular-nums ${tone}`}>
        {value !== undefined ? `${formatHours(value)}${suffix}` : isLoading ? '…' : `0${suffix}`}
      </span>
    </div>
  );
}

/** Status + on-time/late count chips. Status chips toggle the status filter. */
function CountChip({
  label,
  count,
  tone,
  active,
  onClick,
}: {
  label: string;
  count: number;
  tone: string;
  active?: boolean;
  onClick?: () => void;
}): JSX.Element {
  const inner = (
    <>
      <span>{label}</span>
      <span className="font-bold tabular-nums">{count}</span>
    </>
  );
  const base = `inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${tone}`;
  if (!onClick) {
    return <span className={base}>{inner}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${base} cursor-pointer transition hover:brightness-95 ${
        active ? 'ring-2 ring-brand-400' : ''
      }`}
    >
      {inner}
    </button>
  );
}

const STATUS_CHIP_TONES: Record<TimesheetStatus, string> = {
  draft: 'border-slate-300 bg-slate-100 text-slate-700',
  submitted: 'border-sky-200 bg-sky-50 text-sky-700',
  approved: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  rejected: 'border-rose-200 bg-rose-50 text-rose-700',
};

export function TimesheetsPage(): JSX.Element {
  const { can, isAdmin, canSeeMenu, principal } = useAuth();
  const rowLink = useRowLink();
  const canEdit = can('time', 'edit');
  // Admins and anyone with the Approvals menu item at view level or above may
  // see everyone's timesheets; other members are scoped to their own.
  const canSeeAll = canSeeMenu('approvals');
  const currentMemberId = principal?.type === 'member' ? principal.id : '';

  const [page, setPage] = useState(1);
  const [memberIds, setMemberIds] = useState<string[]>(
    canSeeAll || !currentMemberId ? [] : [currentMemberId],
  );
  const [status, setStatus] = useState<TimesheetStatus | ''>('draft');
  const [range, setRange] = useState<QuickRange | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  /** Week back/forward: shift the current week window, or snap to a week around today. */
  const stepWeek = (dir: -1 | 0 | 1): void => {
    setPage(1);
    const base = range?.mode === 'week' ? range.from : mondayOf(todayIso());
    setRange(weekRange(addDaysIso(base, dir * 7)));
  };

  /** Month back/forward: shift the current month window, or snap to a month around today. */
  const stepMonth = (dir: -1 | 0 | 1): void => {
    setPage(1);
    const base = range?.mode === 'month' ? range.from : todayIso();
    setRange(monthRange(base, dir));
  };

  const clearRange = (): void => {
    setPage(1);
    setRange(null);
  };

  // Default the member filter to the logged-in member once the principal
  // resolves. Users can still change or clear it afterward. A saved filter
  // default (below) loads afterwards and overrides this seed.
  const didInitMember = useRef(false);
  useEffect(() => {
    if (didInitMember.current || !principal) {
      return;
    }
    didInitMember.current = true;
    if (currentMemberId) {
      setMemberIds([currentMemberId]);
    }
  }, [principal, currentMemberId]);

  const filterDefaults = useFilterDefaults('timesheets', (saved) => {
    didInitMember.current = true;
    const savedStatus = savedString(saved, 'status');
    if (savedStatus !== null) setStatus(savedStatus as TimesheetStatus | '');
    // Saved range restores the SPECIFIC week/month that was on screen when saved; older
    // saves without an anchor date fall back to the current week/month.
    const savedRange = savedString(saved, 'rangeMode');
    const savedFrom = savedString(saved, 'rangeFrom');
    const anchor = savedFrom && /^\d{4}-\d{2}-\d{2}$/.test(savedFrom) ? savedFrom : todayIso();
    if (savedRange === 'week') setRange(weekRange(anchor));
    else if (savedRange === 'month') setRange(monthRange(anchor));
    else if (savedRange === '') setRange(null);
    // Members without the Approvals menu stay scoped to their own timesheets.
    if (canSeeAll) {
      const savedMembers = savedStringArray(saved, 'memberIds');
      if (savedMembers !== null) setMemberIds(savedMembers);
    }
  });

  // Timesheet users should always see the current week, even before logging any
  // time. Ask the server to ensure that week's draft exists when the page opens.
  const preloadMutation = usePreloadCurrentWeek();
  const didPreload = useRef(false);
  const preload = preloadMutation.mutate;
  useEffect(() => {
    if (didPreload.current || !currentMemberId) {
      return;
    }
    didPreload.current = true;
    preload();
  }, [currentMemberId, preload]);

  const query = useTimesheets({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    memberIds: memberIds.length > 0 ? memberIds : undefined,
    status: status || undefined,
    weekStartFrom: range?.from,
    weekStartTo: range?.to,
    sortBy: 'weekStartDate',
    sortDir: 'desc',
  });
  // The summary deliberately ignores the status filter so the status-count chips stay
  // meaningful (and can act as filter toggles), like the time-entries breakdown cards.
  const summaryQuery = useTimesheetsSummary({
    memberIds: memberIds.length > 0 ? memberIds : undefined,
    weekStartFrom: range?.from,
    weekStartTo: range?.to,
  });
  const summary: TimesheetSummary | undefined = summaryQuery.data;
  const membersQuery = useMemberOptions();
  const submitMutation = useSubmitTimesheet();

  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of membersQuery.data ?? []) {
      map.set(m.id, m.displayName);
    }
    return map;
  }, [membersQuery.data]);

  const timesheets = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const canSubmit = (ts: Timesheet): boolean =>
    canEdit &&
    (ts.status === 'draft' || ts.status === 'rejected') &&
    ts.entryCount > 0 &&
    (canSeeAll || ts.memberId === currentMemberId);

  return (
    <div>
      <PageHeader
        title="Timesheets"
        description="Weekly timesheets group your time entries for submission and approval."
        actions={
          isAdmin ? (
            <Button
              variant="secondary"
              onClick={() => setSettingsOpen(true)}
              title="Timesheet due date & reminders"
              aria-label="Timesheet settings"
            >
              <Icon name="settings" className="h-4 w-4" />
            </Button>
          ) : undefined
        }
      />

      {isAdmin ? (
        <Modal
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          title="Timesheet due date & reminders"
          size="lg"
          footer={
            <Button variant="secondary" onClick={() => setSettingsOpen(false)}>
              Close
            </Button>
          }
        >
          <DueConfigCard />
        </Modal>
      ) : null}

      <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="mb-3 flex flex-wrap items-baseline gap-x-8 gap-y-2">
          <TotalStat
            label="Total Hours (filtered)"
            value={summary?.totalHours}
            isLoading={summaryQuery.isLoading}
          />
          <TotalStat
            label="Billable (filtered)"
            value={summary?.billableHours}
            isLoading={summaryQuery.isLoading}
            tone="text-emerald-700"
          />
          <TotalStat
            label="Non-Billable (filtered)"
            value={summary?.nonBillableHours}
            isLoading={summaryQuery.isLoading}
            tone="text-amber-700"
          />
          <TotalStat
            label="% Billable (filtered)"
            value={
              summary ? percentBillable(summary.billableHours, summary.totalHours) : undefined
            }
            isLoading={summaryQuery.isLoading}
            tone="text-emerald-700"
            suffix="%"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {TIMESHEET_STATUSES.map((s) => (
            <CountChip
              key={s}
              label={STATUS_LABELS[s]}
              count={summary?.statusCounts[s] ?? 0}
              tone={STATUS_CHIP_TONES[s]}
              active={status === s}
              onClick={() => {
                setPage(1);
                setStatus(status === s ? '' : s);
              }}
            />
          ))}
          <span className="mx-1 h-5 w-px bg-slate-300" aria-hidden="true" />
          <CountChip
            label="On time"
            count={summary?.onTimeCount ?? 0}
            tone="border-emerald-200 bg-emerald-50 text-emerald-700"
          />
          <CountChip
            label="Late"
            count={summary?.pastDueCount ?? 0}
            tone="border-rose-200 bg-rose-50 text-rose-700"
          />
          {summary && summary.noTimingCount > 0 ? (
            <span className="text-xs text-slate-400">
              {summary.noTimingCount} without submission timing
            </span>
          ) : null}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
          <div className="flex items-center gap-1">
            <Button variant="secondary" onClick={() => stepWeek(-1)} title="Previous week">
              ‹
            </Button>
            <Button
              variant={range?.mode === 'week' ? 'primary' : 'secondary'}
              onClick={() => stepWeek(0)}
              title="Show one week at a time"
            >
              Week
            </Button>
            <Button variant="secondary" onClick={() => stepWeek(1)} title="Next week">
              ›
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="secondary" onClick={() => stepMonth(-1)} title="Previous month">
              ‹
            </Button>
            <Button
              variant={range?.mode === 'month' ? 'primary' : 'secondary'}
              onClick={() => stepMonth(0)}
              title="Show one month at a time"
            >
              Month
            </Button>
            <Button variant="secondary" onClick={() => stepMonth(1)} title="Next month">
              ›
            </Button>
          </div>
          {range ? (
            <>
              <span className="text-sm font-medium text-slate-700">{rangeLabel(range)}</span>
              <Button variant="secondary" onClick={clearRange}>
                All dates
              </Button>
            </>
          ) : (
            <span className="text-sm text-slate-400">All dates</span>
          )}
          <span className="mx-1 h-6 w-px bg-slate-200" aria-hidden="true" />
          {canSeeAll ? (
            <MemberMultiFilter
              selected={memberIds}
              onChange={(next) => {
                setPage(1);
                setMemberIds(next);
              }}
              allLabel="All members"
            />
          ) : null}
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value as TimesheetStatus | '');
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Un-submitted</option>
          </select>
          <FilterDefaultsButtons
            handle={filterDefaults}
            onSave={() =>
              filterDefaults.save({
                status,
                memberIds,
                rangeMode: range?.mode ?? '',
                rangeFrom: range?.from ?? '',
              })
            }
          />
        </div>

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Week of</th>
              {canSeeAll ? <th className="px-4 py-3 font-medium">Member</th> : null}
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Due</th>
              <th className="px-4 py-3 font-medium text-right">Hours</th>
              <th className="px-4 py-3 font-medium text-right">Billable</th>
              <th className="px-4 py-3 font-medium text-right">Entries</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={canSeeAll ? 8 : 7}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={timesheets.length === 0}
              emptyTitle="No timesheets found"
              emptyDescription="Timesheets appear here once you log time for a week."
            >
              {timesheets.map((ts) => (
                <tr
                  key={ts.id}
                  className="cursor-pointer align-top hover:bg-slate-50"
                  {...rowLink(`/timesheets/${ts.id}`)}
                >
                  <td className="px-4 py-3 tabular-nums text-slate-900">
                    {formatDate(ts.weekStartDate)}
                  </td>
                  {canSeeAll ? (
                    <td className="px-4 py-3 text-slate-600">
                      {memberNames.get(ts.memberId) ?? '—'}
                    </td>
                  ) : null}
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONES[ts.status]}>{STATUS_LABELS[ts.status]}</Badge>
                    {ts.status === 'rejected' && ts.rejectedReason ? (
                      <p className="mt-1 max-w-xs text-xs text-rose-600">{ts.rejectedReason}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <DueIndicator timesheet={ts} />
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                    {ts.totalHours}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {ts.billableHours}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {ts.entryCount}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canSubmit(ts) ? (
                      <Button
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          submitMutation.mutate(ts.id);
                        }}
                        isLoading={submitMutation.isPending && submitMutation.variables === ts.id}
                      >
                        Submit
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && timesheets.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>
      <DetailOverlay listPath="/timesheets" />
    </div>
  );
}
